import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminUserSummary } from "@/lib/api/admin";
import { useAdminUsers } from "./useAdminUsers";

export const ADMIN_TABS = [
  "overview",
  "people",
  "growth",
  "finance",
  "content",
  "system",
] as const;
export type AdminTab = (typeof ADMIN_TABS)[number];

const TAB_SET: ReadonlySet<string> = new Set(ADMIN_TABS);
const LEGACY_TAB_MAP: Readonly<Record<string, AdminTab>> = {
  health: "overview",
  users: "people",
  kols: "growth",
  library: "content",
  quiz: "content",
  social: "content",
  notifications: "people",
  revenue: "finance",
  orders: "finance",
};

export function resolveAdminTab(value: string | null): AdminTab {
  if (value && TAB_SET.has(value)) return value as AdminTab;
  if (value && value in LEGACY_TAB_MAP) return LEGACY_TAB_MAP[value];
  return "overview";
}

export interface AdminUiContextValue {
  tab: AdminTab;
  setTab: (tab: AdminTab, panel?: string) => void;
  selectedUserId: number | null;
  setSelectedUserId: (id: number | null) => void;
  selectedKolId: string | null;
  setSelectedKolId: (id: string | null, panel?: string) => void;
  openChannelCustomers: (code: string) => void;
  openCustomerOrders: (id: number) => void;
  kolCreateOpen: boolean;
  setKolCreateOpen: (open: boolean) => void;
  inviteOpen: boolean;
  setInviteOpen: (open: boolean) => void;
  expiringSoon: readonly AdminUserSummary[];
  recentSignups: readonly AdminUserSummary[];
  expiredUsers: readonly AdminUserSummary[];
  conversionRate: number;
  avgRevPerUser: number;
  // Stable `Date.now()` snapshot — set once on provider mount so the
  // expires-in-Nd labels don't re-render every microtask.
  now: number;
}

const AdminUiContext = createContext<AdminUiContextValue | null>(null);

export function useAdminUi(): AdminUiContextValue {
  const ctx = useContext(AdminUiContext);
  if (!ctx) throw new Error("useAdminUi must be used inside AdminUiProvider");
  return ctx;
}

export function AdminUiProvider({ children }: { children: ReactNode }) {
  const { users, userCount, revenue } = useAdminUsers();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = resolveAdminTab(searchParams.get("tab"));

  const setTab = useCallback(
    (next: AdminTab, panel?: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set("tab", next);
          params.delete("kol");
          params.delete("user");
          params.delete("kol_panel");
          params.delete("channel");
          params.delete("customer");
          if (panel) params.set("panel", panel);
          else params.delete("panel");
          if (next !== "content") params.delete("section");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const rawUserId = Number(searchParams.get("user"));
  const selectedUserId =
    Number.isSafeInteger(rawUserId) && rawUserId > 0 ? rawUserId : null;
  const setSelectedUserId = useCallback(
    (id: number | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (id) params.set("user", String(id));
          else params.delete("user");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Keep the open creator in the URL so returning from a share card restores
  // the same detail sheet, including after the admin route remounts.
  const selectedKolId = searchParams.get("kol");
  const setSelectedKolId = useCallback(
    (id: string | null, panel?: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (id) params.delete("user");
          if (panel && id) params.set("kol_panel", panel);
          else params.delete("kol_panel");
          if (id) params.set("kol", id);
          else params.delete("kol");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  const openChannelCustomers = useCallback(
    (code: string) => {
      setSearchParams({ tab: "people", panel: "customers", channel: code });
    },
    [setSearchParams],
  );
  const openCustomerOrders = useCallback(
    (id: number) => {
      setSearchParams({
        tab: "finance",
        panel: "orders",
        customer: String(id),
      });
    },
    [setSearchParams],
  );
  const [kolCreateOpen, setKolCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [now] = useState(() => Date.now());

  const expiringSoon = useMemo(
    () =>
      users
        .filter((u) => {
          if (!u.expires_at) return false;
          const days = Math.ceil(
            (new Date(u.expires_at).getTime() - now) / 86400000,
          );
          return days > 0 && days <= 7;
        })
        .slice()
        .sort(
          (a, b) =>
            new Date(a.expires_at!).getTime() -
            new Date(b.expires_at!).getTime(),
        ),
    [users, now],
  );

  const recentSignups = useMemo(() => {
    const cutoff = now - 7 * 86400000;
    return users
      .filter((u) => new Date(u.created_at).getTime() > cutoff)
      .slice()
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
  }, [users, now]);

  const expiredUsers = useMemo(
    () =>
      users.filter((u) => {
        if (!u.expires_at) return false;
        return new Date(u.expires_at).getTime() <= now;
      }),
    [users, now],
  );

  const conversionRate =
    userCount === 0 ? 0 : Math.round((revenue.paying_users / userCount) * 100);

  const avgRevPerUser =
    revenue.paying_users === 0
      ? 0
      : revenue.total_cents / 100 / revenue.paying_users;

  const value = useMemo<AdminUiContextValue>(
    () => ({
      tab,
      setTab,
      selectedUserId,
      setSelectedUserId,
      selectedKolId,
      setSelectedKolId,
      openChannelCustomers,
      openCustomerOrders,
      kolCreateOpen,
      setKolCreateOpen,
      inviteOpen,
      setInviteOpen,
      expiringSoon,
      recentSignups,
      expiredUsers,
      conversionRate,
      avgRevPerUser,
      now,
    }),
    [
      tab,
      setTab,
      selectedUserId,
      setSelectedUserId,
      selectedKolId,
      setSelectedKolId,
      openChannelCustomers,
      openCustomerOrders,
      kolCreateOpen,
      inviteOpen,
      expiringSoon,
      recentSignups,
      expiredUsers,
      conversionRate,
      avgRevPerUser,
      now,
    ],
  );

  return (
    <AdminUiContext.Provider value={value}>{children}</AdminUiContext.Provider>
  );
}
